import { Type, Tier } from "./types";

export type Endpoint = {
  name: string;
  tier: Tier; // Minimum tier required
};

export type SportPricing = {
  type: Type;
  displayName: string;
  fullName?: string;
  abbreviation?: string;
  category:
    | "basketball"
    | "football"
    | "soccer"
    | "baseball"
    | "hockey"
    | "combat"
    | "tennis"
    | "stories"
    | "other";
  endpoints: Endpoint[];
  docUrl?: string;
};

// Documentation URLs for each sport
export const DOC_URLS: Partial<Record<Type, string>> = {
  cba: "https://cba.balldontlie.io",
  euroleague: "https://euroleague.balldontlie.io",
  eurocup: "https://eurocup.balldontlie.io",
  bcl: "https://bcl.balldontlie.io",
  acb: "https://acb.balldontlie.io",
  bsl: "https://bsl.balldontlie.io",
  gbl: "https://gbl.balldontlie.io",
  lba: "https://lba.balldontlie.io",
  bbl: "https://bbl.balldontlie.io",
  lnb: "https://lnb.balldontlie.io",
  aba: "https://aba.balldontlie.io",
  lkl: "https://lkl.balldontlie.io",
  vtb: "https://vtb.balldontlie.io",
  bnxt: "https://bnxt.balldontlie.io",
  israel: "https://israel.balldontlie.io",

  nba: "https://nba.balldontlie.io",
  nfl: "https://nfl.balldontlie.io",
  mlb: "https://mlb.balldontlie.io",
  epl: "https://epl.balldontlie.io",
  nhl: "https://nhl.balldontlie.io",
  wnba: "https://wnba.balldontlie.io",
  ncaaf: "https://ncaaf.balldontlie.io",
  ncaab: "https://ncaab.balldontlie.io",
  ncaaw: "https://ncaaw.balldontlie.io",
  mma: "https://mma.balldontlie.io",
  fifa: "https://fifa.balldontlie.io",
  cs: "https://cs.balldontlie.io",
  valorant: "https://valorant.balldontlie.io",
  laliga: "https://laliga.balldontlie.io",
  seriea: "https://seriea.balldontlie.io",
  ucl: "https://ucl.balldontlie.io",
  bundesliga: "https://bundesliga.balldontlie.io",
  ligue1: "https://ligue1.balldontlie.io",
  mls: "https://mls.balldontlie.io",
  lol: "https://lol.balldontlie.io",
  dota2: "https://dota2.balldontlie.io",
  pga: "https://pga.balldontlie.io",
  atp: "https://atp.balldontlie.io",
  wta: "https://wta.balldontlie.io",
  f1: "https://f1.balldontlie.io",
  cbb: "https://cbb.balldontlie.io",
};

export const PRICING: Record<Type, SportPricing> = {
  cba: {
    type: "cba", displayName: "CBA",
    fullName: "Chinese Basketball Association",
    abbreviation: "CBA", category: "basketball",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Games", tier: "free" },
      { name: "Team Rosters", tier: "paid" },
      { name: "Standings", tier: "paid" },
      { name: "Lineups", tier: "paid" },
      { name: "Scoring", tier: "paid" },
      { name: "Player Stats", tier: "paid+" },
      { name: "Team Stats", tier: "paid+" },
      { name: "Season Averages", tier: "paid+" },
    ],
  },
  euroleague: {
    type: "euroleague",
    displayName: "EuroLeague",
    fullName: "EuroLeague",
    abbreviation: "",
    category: "basketball",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Standings", tier: "free" },
      { name: "Games", tier: "paid" },
      { name: "Lineups", tier: "paid" },
      { name: "Scoring", tier: "paid" },
      { name: "Player Stats", tier: "paid+" },
      { name: "Team Stats", tier: "paid+" },
    ],
  },
  eurocup: {
    type: "eurocup",
    displayName: "EuroCup",
    fullName: "EuroCup",
    abbreviation: "",
    category: "basketball",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Standings", tier: "free" },
      { name: "Games", tier: "paid" },
      { name: "Lineups", tier: "paid" },
      { name: "Scoring", tier: "paid" },
      { name: "Player Stats", tier: "paid+" },
      { name: "Team Stats", tier: "paid+" },
    ],
  },
  bcl: {
    type: "bcl",
    displayName: "Basketball Champions League",
    fullName: "Basketball Champions League",
    abbreviation: "BCL",
    category: "basketball",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Standings", tier: "free" },
      { name: "Games", tier: "paid" },
      { name: "Lineups", tier: "paid" },
      { name: "Scoring", tier: "paid" },
      { name: "Player Stats", tier: "paid+" },
      { name: "Team Stats", tier: "paid+" },
    ],
  },
  acb: {
    type: "acb",
    displayName: "Liga ACB",
    fullName: "Liga ACB",
    abbreviation: "ACB",
    category: "basketball",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Standings", tier: "free" },
      { name: "Games", tier: "paid" },
      { name: "Lineups", tier: "paid" },
      { name: "Scoring", tier: "paid" },
      { name: "Player Stats", tier: "paid+" },
      { name: "Team Stats", tier: "paid+" },
    ],
  },
  bsl: {
    type: "bsl",
    displayName: "Turkish Basketball Super League",
    fullName: "Turkish Basketball Super League",
    abbreviation: "BSL",
    category: "basketball",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Standings", tier: "free" },
      { name: "Games", tier: "paid" },
      { name: "Lineups", tier: "paid" },
      { name: "Scoring", tier: "paid" },
      { name: "Player Stats", tier: "paid+" },
      { name: "Team Stats", tier: "paid+" },
    ],
  },
  gbl: {
    type: "gbl",
    displayName: "Greek Basketball League",
    fullName: "Greek Basketball League",
    abbreviation: "GBL",
    category: "basketball",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Standings", tier: "free" },
      { name: "Games", tier: "paid" },
      { name: "Lineups", tier: "paid" },
      { name: "Scoring", tier: "paid" },
      { name: "Player Stats", tier: "paid+" },
      { name: "Team Stats", tier: "paid+" },
    ],
  },
  lba: {
    type: "lba",
    displayName: "Lega Basket Serie A",
    fullName: "Lega Basket Serie A",
    abbreviation: "LBA",
    category: "basketball",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Standings", tier: "free" },
      { name: "Games", tier: "paid" },
      { name: "Lineups", tier: "paid" },
      { name: "Scoring", tier: "paid" },
      { name: "Player Stats", tier: "paid+" },
      { name: "Team Stats", tier: "paid+" },
    ],
  },
  bbl: {
    type: "bbl",
    displayName: "Basketball Bundesliga",
    fullName: "Basketball Bundesliga",
    abbreviation: "BBL",
    category: "basketball",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Standings", tier: "free" },
      { name: "Games", tier: "paid" },
      { name: "Lineups", tier: "paid" },
      { name: "Scoring", tier: "paid" },
      { name: "Player Stats", tier: "paid+" },
      { name: "Team Stats", tier: "paid+" },
    ],
  },
  lnb: {
    type: "lnb",
    displayName: "LNB Élite",
    fullName: "LNB Élite",
    abbreviation: "LNB",
    category: "basketball",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Standings", tier: "free" },
      { name: "Games", tier: "paid" },
      { name: "Lineups", tier: "paid" },
      { name: "Scoring", tier: "paid" },
      { name: "Player Stats", tier: "paid+" },
      { name: "Team Stats", tier: "paid+" },
    ],
  },
  aba: {
    type: "aba",
    displayName: "ABA League",
    fullName: "ABA League",
    abbreviation: "ABA",
    category: "basketball",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Standings", tier: "free" },
      { name: "Games", tier: "paid" },
      { name: "Lineups", tier: "paid" },
      { name: "Scoring", tier: "paid" },
      { name: "Player Stats", tier: "paid+" },
      { name: "Team Stats", tier: "paid+" },
    ],
  },
  lkl: {
    type: "lkl",
    displayName: "Lithuanian Basketball League",
    fullName: "Lithuanian Basketball League",
    abbreviation: "LKL",
    category: "basketball",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Standings", tier: "free" },
      { name: "Games", tier: "paid" },
      { name: "Lineups", tier: "paid" },
      { name: "Scoring", tier: "paid" },
      { name: "Player Stats", tier: "paid+" },
      { name: "Team Stats", tier: "paid+" },
    ],
  },
  vtb: {
    type: "vtb",
    displayName: "VTB United League",
    fullName: "VTB United League",
    abbreviation: "VTB",
    category: "basketball",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Standings", tier: "free" },
      { name: "Games", tier: "paid" },
      { name: "Lineups", tier: "paid" },
      { name: "Scoring", tier: "paid" },
      { name: "Player Stats", tier: "paid+" },
      { name: "Team Stats", tier: "paid+" },
    ],
  },
  bnxt: {
    type: "bnxt",
    displayName: "BNXT League",
    fullName: "BNXT League",
    abbreviation: "BNXT",
    category: "basketball",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Standings", tier: "free" },
      { name: "Games", tier: "paid" },
      { name: "Lineups", tier: "paid" },
      { name: "Scoring", tier: "paid" },
      { name: "Player Stats", tier: "paid+" },
      { name: "Team Stats", tier: "paid+" },
    ],
  },
  israel: {
    type: "israel",
    displayName: "Israeli Basketball Premier League",
    fullName: "Israeli Basketball Premier League",
    abbreviation: "",
    category: "basketball",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Standings", tier: "free" },
      { name: "Games", tier: "paid" },
      { name: "Lineups", tier: "paid" },
      { name: "Scoring", tier: "paid" },
      { name: "Player Stats", tier: "paid+" },
      { name: "Team Stats", tier: "paid+" },
    ],
  },

  nba: {
    type: "nba",
    displayName: "NBA",
    fullName: "National Basketball Association",
    abbreviation: "NBA",
    category: "basketball",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Games", tier: "free" },
      { name: "Game Player Stats", tier: "paid" },
      { name: "Active Players", tier: "paid" },
      { name: "Player Injuries", tier: "paid" },
      { name: "Season Averages", tier: "paid+" },
      { name: "Game Advanced Stats", tier: "paid+" },
      { name: "Live Box Scores", tier: "paid+" },
      { name: "Lineups", tier: "paid+" },
      { name: "Team Standings", tier: "paid+" },
      { name: "Leaders", tier: "paid+" },
      { name: "Betting Odds", tier: "paid+" },
      { name: "Player Props", tier: "paid+" },
      { name: "Team Contracts", tier: "paid+" },
      { name: "Player Contracts", tier: "paid+" },
      { name: "Player Contract Aggregates", tier: "paid+" },
      { name: "Team Season Averages", tier: "paid+" },
    ],
  },
  nfl: {
    type: "nfl",
    displayName: "NFL",
    fullName: "National Football League",
    abbreviation: "NFL",
    category: "football",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Games", tier: "free" },
      { name: "Player Injuries", tier: "paid" },
      { name: "Active Players", tier: "paid" },
      { name: "Team Standings", tier: "paid" },
      { name: "Stats", tier: "paid" },
      { name: "Season Stats", tier: "paid" },
      { name: "Team Stats", tier: "paid" },
      { name: "Team Season Stats", tier: "paid" },
      { name: "Team Roster", tier: "paid+" },
      { name: "Advanced Rushing Stats", tier: "paid+" },
      { name: "Advanced Passing Stats", tier: "paid+" },
      { name: "Advanced Receiving Stats", tier: "paid+" },
      { name: "Plays", tier: "paid+" },
      { name: "Betting Odds", tier: "paid+" },
      { name: "Player Props", tier: "paid+" },
    ],
  },
  mlb: {
    type: "mlb",
    displayName: "MLB",
    fullName: "Major League Baseball",
    abbreviation: "MLB",
    category: "baseball",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Games", tier: "free" },
      { name: "Player Injuries", tier: "paid" },
      { name: "Active Players", tier: "paid" },
      { name: "Team Standings", tier: "paid" },
      { name: "Player Stats", tier: "paid" },
      { name: "Player Season Stats", tier: "paid+" },
      { name: "Team Season Stats", tier: "paid+" },
      { name: "Player Splits", tier: "paid+" },
      { name: "Player vs Player", tier: "paid+" },
    ],
  },
  cbb: {
    type: "cbb",
    displayName: "College Baseball",
    fullName: "College Baseball",
    abbreviation: "CBB",
    category: "baseball",
    endpoints: [
      { name: "Conferences", tier: "free" },
      { name: "Teams", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Standings", tier: "free" },
      { name: "Games", tier: "paid" },
      { name: "Rankings", tier: "paid" },
      { name: "Active Players", tier: "paid" },
      { name: "Plays", tier: "paid" },
      { name: "Player Batting Stats", tier: "paid+" },
      { name: "Player Pitching Stats", tier: "paid+" },
      { name: "Team Batting Stats", tier: "paid+" },
      { name: "Team Pitching Stats", tier: "paid+" },
      { name: "Team Fielding Stats", tier: "paid+" },
      { name: "Player Season Batting Stats", tier: "paid+" },
      { name: "Player Season Pitching Stats", tier: "paid+" },
      { name: "Team Season Stats", tier: "paid+" },
      { name: "Betting Odds", tier: "paid+" },
    ],
  },
  epl: {
    type: "epl",
    displayName: "EPL",
    fullName: "English Premier League",
    abbreviation: "EPL",
    category: "soccer",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Team Players", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Games", tier: "free" },
      { name: "Team Standings", tier: "paid" },
      { name: "Player Season Stats", tier: "paid" },
      { name: "Player Stat Leaders", tier: "paid" },
      { name: "Team Season Stats", tier: "paid" },
      { name: "Team Stat Leaders", tier: "paid" },
      { name: "Game Lineups", tier: "paid+" },
      { name: "Game Goals", tier: "paid+" },
      { name: "Game Team Stats", tier: "paid+" },
      { name: "Game Player Stats", tier: "paid+" },
      { name: "Betting Odds", tier: "paid+" },
      { name: "Player Props", tier: "paid+" },
    ],
  },
  nhl: {
    type: "nhl",
    displayName: "NHL",
    fullName: "National Hockey League",
    abbreviation: "NHL",
    category: "hockey",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Games", tier: "paid" },
      { name: "Standings", tier: "paid" },
      { name: "Box Scores", tier: "paid" },
      { name: "Play-by-Play", tier: "paid+" },
      { name: "Player Injuries", tier: "paid+" },
      { name: "Player Season Stats", tier: "paid+" },
      { name: "Team Season Stats", tier: "paid+" },
      { name: "Player Stats Leaders", tier: "paid+" },
      { name: "Team Stats Leaders", tier: "paid+" },
      { name: "Betting Odds", tier: "paid+" },
      { name: "Player Props", tier: "paid+" },
    ],
  },
  wnba: {
    type: "wnba",
    displayName: "WNBA",
    fullName: "Women’s National Basketball Association",
    abbreviation: "WNBA",
    category: "basketball",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Games", tier: "free" },
      { name: "Standings", tier: "paid" },
      { name: "Player Injuries", tier: "paid" },
      { name: "Active Players", tier: "paid" },
      { name: "Play-by-Play", tier: "paid" },
      { name: "Player Stats", tier: "paid+" },
      { name: "Team Stats", tier: "paid+" },
      { name: "Player Season Stats", tier: "paid+" },
      { name: "Team Season Stats", tier: "paid+" },
    ],
  },
  ncaaf: {
    type: "ncaaf",
    displayName: "NCAAF",
    fullName: "NCAA Football",
    abbreviation: "NCAAF",
    category: "football",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Conferences", tier: "free" },
      { name: "Games", tier: "paid" },
      { name: "Rankings", tier: "paid" },
      { name: "Play-by-Play", tier: "paid" },
      { name: "Player Stats", tier: "paid+" },
      { name: "Team Stats", tier: "paid+" },
      { name: "Player Season Stats", tier: "paid+" },
      { name: "Team Season Stats", tier: "paid+" },
      { name: "Betting Odds", tier: "paid+" },
    ],
  },
  ncaab: {
    type: "ncaab",
    displayName: "NCAAB",
    fullName: "NCAA Men’s Basketball",
    abbreviation: "NCAAB",
    category: "basketball",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Conferences", tier: "free" },
      { name: "Games", tier: "paid" },
      { name: "Standings", tier: "paid" },
      { name: "Rankings", tier: "paid" },
      { name: "Play-by-Play", tier: "paid" },
      { name: "Player Stats", tier: "paid+" },
      { name: "Team Stats", tier: "paid+" },
      { name: "Player Season Stats", tier: "paid+" },
      { name: "Team Season Stats", tier: "paid+" },
      { name: "March Madness Bracket", tier: "paid+" },
      { name: "Betting Odds", tier: "paid+" },
    ],
  },
  ncaaw: {
    type: "ncaaw",
    displayName: "NCAAW",
    fullName: "NCAA Women’s Basketball",
    abbreviation: "NCAAW",
    category: "basketball",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Conferences", tier: "free" },
      { name: "Games", tier: "paid" },
      { name: "Standings", tier: "paid" },
      { name: "Rankings", tier: "paid" },
      { name: "Play-by-Play", tier: "paid" },
      { name: "Player Stats", tier: "paid+" },
      { name: "Team Stats", tier: "paid+" },
      { name: "Player Season Stats", tier: "paid+" },
      { name: "Team Season Stats", tier: "paid+" },
      { name: "Women's Tournament Bracket", tier: "paid+" },
      { name: "Betting Odds", tier: "paid+" },
    ],
  },
  mma: {
    type: "mma",
    displayName: "MMA",
    fullName: "Mixed Martial Arts",
    abbreviation: "MMA",
    category: "combat",
    endpoints: [
      { name: "Leagues", tier: "free" },
      { name: "Events", tier: "free" },
      { name: "Fighters", tier: "free" },
      { name: "Fights", tier: "paid" },
      { name: "Rankings", tier: "paid" },
      { name: "Fight Statistics", tier: "paid+" },
      { name: "Betting Odds", tier: "paid+" },
    ],
  },
  fifa: {
    type: "fifa",
    displayName: "World Cup 2026",
    fullName: "FIFA World Cup",
    abbreviation: "FIFA",
    category: "soccer",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Stadiums", tier: "free" },
      { name: "Group Standings", tier: "paid" },
      { name: "Matches", tier: "paid+" },
      { name: "Betting Odds", tier: "paid+" },
      { name: "Futures Odds", tier: "paid+" },
    ],
  },
  laliga: {
    type: "laliga",
    displayName: "La Liga",
    fullName: "La Liga",
    abbreviation: "",
    category: "soccer",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Rosters", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Standings", tier: "free" },
      { name: "Matches", tier: "paid" },
      { name: "Match Events", tier: "paid" },
      { name: "Match Lineups", tier: "paid" },
      { name: "Player Match Stats", tier: "paid+" },
      { name: "Team Match Stats", tier: "paid+" },
      { name: "Betting Odds", tier: "paid+" },
      { name: "Player Props", tier: "paid+" },
    ],
  },
  seriea: {
    type: "seriea",
    displayName: "Serie A",
    fullName: "Serie A",
    abbreviation: "",
    category: "soccer",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Rosters", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Standings", tier: "free" },
      { name: "Matches", tier: "paid" },
      { name: "Match Events", tier: "paid" },
      { name: "Match Lineups", tier: "paid" },
      { name: "Player Match Stats", tier: "paid+" },
      { name: "Team Match Stats", tier: "paid+" },
      { name: "Betting Odds", tier: "paid+" },
      { name: "Player Props", tier: "paid+" },
    ],
  },
  ucl: {
    type: "ucl",
    displayName: "Champions League",
    fullName: "UEFA Champions League",
    abbreviation: "UCL",
    category: "soccer",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Rosters", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Standings", tier: "free" },
      { name: "Matches", tier: "paid" },
      { name: "Match Events", tier: "paid" },
      { name: "Match Lineups", tier: "paid" },
      { name: "Player Match Stats", tier: "paid+" },
      { name: "Team Match Stats", tier: "paid+" },
      { name: "Betting Odds", tier: "paid+" },
      { name: "Player Props", tier: "paid+" },
      { name: "Futures Odds", tier: "paid+" },
    ],
  },
  bundesliga: {
    type: "bundesliga",
    displayName: "Bundesliga",
    fullName: "Bundesliga",
    abbreviation: "",
    category: "soccer",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Rosters", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Standings", tier: "free" },
      { name: "Matches", tier: "paid" },
      { name: "Match Events", tier: "paid" },
      { name: "Match Lineups", tier: "paid" },
      { name: "Player Match Stats", tier: "paid+" },
      { name: "Team Match Stats", tier: "paid+" },
      { name: "Betting Odds", tier: "paid+" },
      { name: "Player Props", tier: "paid+" },
    ],
  },
  ligue1: {
    type: "ligue1",
    displayName: "Ligue 1",
    fullName: "Ligue 1",
    abbreviation: "",
    category: "soccer",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Rosters", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Standings", tier: "free" },
      { name: "Matches", tier: "paid" },
      { name: "Match Events", tier: "paid" },
      { name: "Match Lineups", tier: "paid" },
      { name: "Player Match Stats", tier: "paid+" },
      { name: "Team Match Stats", tier: "paid+" },
      { name: "Betting Odds", tier: "paid+" },
      { name: "Player Props", tier: "paid+" },
    ],
  },
  mls: {
    type: "mls",
    displayName: "MLS",
    fullName: "Major League Soccer",
    abbreviation: "MLS",
    category: "soccer",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Rosters", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Standings", tier: "free" },
      { name: "Matches", tier: "paid" },
      { name: "Match Events", tier: "paid" },
      { name: "Match Lineups", tier: "paid" },
      { name: "Player Match Stats", tier: "paid+" },
      { name: "Team Match Stats", tier: "paid+" },
      { name: "Betting Odds", tier: "paid+" },
      { name: "Player Props", tier: "paid+" },
    ],
  },
  cs: {
    type: "cs",
    displayName: "CS2",
    fullName: "Counter-Strike 2",
    abbreviation: "CS2",
    category: "other",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Tournaments", tier: "free" },
      { name: "Tournament Teams", tier: "free" },
      { name: "Team Map Pool", tier: "paid" },
      { name: "Rankings", tier: "paid" },
      { name: "Matches", tier: "paid+" },
      { name: "Match Maps", tier: "paid+" },
      { name: "Match Map Stats", tier: "paid+" },
      { name: "Player Match Stats", tier: "paid+" },
      { name: "Player Match Map Stats", tier: "paid+" },
      { name: "Player Accuracy Stats", tier: "paid+" },
    ],
  },
  valorant: {
    type: "valorant",
    displayName: "Valorant",
    fullName: "Valorant",
    abbreviation: "",
    category: "other",
    endpoints: [
      { name: "Regions", tier: "free" },
      { name: "Countries", tier: "free" },
      { name: "Series", tier: "free" },
      { name: "Maps", tier: "free" },
      { name: "Agents", tier: "free" },
      { name: "Weapons", tier: "free" },
      { name: "Teams", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Player Profiles", tier: "free" },
      { name: "Player Crosshairs", tier: "free" },
      { name: "Agent Stats", tier: "paid" },
      { name: "Player Transfers", tier: "paid" },
      { name: "Tournaments", tier: "paid" },
      { name: "Tournament Stages", tier: "paid" },
      { name: "Tournament Teams", tier: "paid" },
      { name: "Tournament Prizes", tier: "paid" },
      { name: "Tournament Roster", tier: "paid" },
      { name: "Matches", tier: "paid+" },
      { name: "Match Maps", tier: "paid+" },
      { name: "Live Matches", tier: "paid+" },
      { name: "Player Overall Stats", tier: "paid+" },
      { name: "Player Match Stats", tier: "paid+" },
      { name: "Player Match Map Stats", tier: "paid+" },
      { name: "Rounds", tier: "paid+" },
      { name: "Player Round Stats", tier: "paid+" },
      { name: "Team Round Stats", tier: "paid+" },
      { name: "Player Match Weapon Stats", tier: "paid+" },
    ],
  },
  lol: {
    type: "lol",
    displayName: "LoL",
    fullName: "League of Legends",
    abbreviation: "LoL",
    category: "other",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Items", tier: "free" },
      { name: "Rune Paths", tier: "free" },
      { name: "Runes", tier: "free" },
      { name: "Spells", tier: "free" },
      { name: "Champions", tier: "free" },
      { name: "Tournaments", tier: "paid" },
      { name: "Tournament Teams", tier: "paid" },
      { name: "Tournament Roster", tier: "paid" },
      { name: "Champion Stats", tier: "paid" },
      { name: "Matches", tier: "paid+" },
      { name: "Match Maps", tier: "paid+" },
      { name: "Player Match Map Stats", tier: "paid+" },
      { name: "Team Match Map Stats", tier: "paid+" },
      { name: "Player Overall Stats", tier: "paid+" },
    ],
  },
  dota2: {
    type: "dota2",
    displayName: "Dota 2",
    fullName: "Dota 2",
    abbreviation: "",
    category: "other",
    endpoints: [
      { name: "Teams", tier: "free" },
      { name: "Players", tier: "free" },
      { name: "Heroes", tier: "free" },
      { name: "Items", tier: "free" },
      { name: "Abilities", tier: "free" },
      { name: "Tournaments", tier: "paid" },
      { name: "Tournament Teams", tier: "paid" },
      { name: "Tournament Roster", tier: "paid" },
      { name: "Hero Stats", tier: "paid" },
      { name: "Matches", tier: "paid+" },
      { name: "Match Maps", tier: "paid+" },
      { name: "Player Match Map Stats", tier: "paid+" },
      { name: "Team Match Map Stats", tier: "paid+" },
      { name: "Player Overall Stats", tier: "paid+" },
    ],
  },
  pga: {
    type: "pga",
    displayName: "PGA Tour",
    fullName: "PGA Tour",
    abbreviation: "",
    category: "other",
    endpoints: [
      { name: "Players", tier: "free" },
      { name: "Tournaments", tier: "free" },
      { name: "Courses", tier: "free" },
      { name: "Tournament Results", tier: "paid" },
      { name: "Tournament Course Stats", tier: "paid" },
      { name: "Course Holes", tier: "paid" },
      { name: "Player Round Results", tier: "paid+" },
      { name: "Player Round Stats", tier: "paid+" },
      { name: "Player Season Stats", tier: "paid+" },
      { name: "Player Scorecards", tier: "paid+" },
    ],
  },
  atp: {
    type: "atp",
    displayName: "ATP Tennis",
    fullName: "Association of Tennis Professionals",
    abbreviation: "ATP",
    category: "tennis",
    endpoints: [
      { name: "Players", tier: "free" },
      { name: "Tournaments", tier: "free" },
      { name: "Rankings", tier: "free" },
      { name: "Matches", tier: "paid" },
      { name: "ATP Race", tier: "paid" },
      { name: "Match Statistics", tier: "paid+" },
      { name: "Player Career Stats", tier: "paid+" },
      { name: "Head to Head", tier: "paid+" },
      { name: "Betting Odds", tier: "paid+" },
    ],
  },
  wta: {
    type: "wta",
    displayName: "WTA Tennis",
    fullName: "Women’s Tennis Association",
    abbreviation: "WTA",
    category: "tennis",
    endpoints: [
      { name: "Players", tier: "free" },
      { name: "Tournaments", tier: "free" },
      { name: "Rankings", tier: "free" },
      { name: "Matches", tier: "paid" },
      { name: "Match Statistics", tier: "paid+" },
      { name: "Head to Head", tier: "paid+" },
      { name: "Betting Odds", tier: "paid+" },
    ],
  },
  f1: {
    type: "f1",
    displayName: "Formula 1",
    fullName: "Formula 1",
    abbreviation: "F1",
    category: "other",
    endpoints: [
      { name: "Drivers", tier: "free" },
      { name: "Teams", tier: "free" },
      { name: "Circuits", tier: "free" },
      { name: "Seasons", tier: "free" },
      { name: "Events", tier: "free" },
      { name: "Sessions", tier: "paid" },
      { name: "Session Results", tier: "paid" },
      { name: "Driver Standings", tier: "paid" },
      { name: "Team Standings", tier: "paid" },
      { name: "Lap Times", tier: "paid+" },
      { name: "Qualifying Results", tier: "paid+" },
      { name: "Pit Stops", tier: "paid+" },
      { name: "Tire Stints", tier: "paid+" },
      { name: "Session Weather", tier: "paid+" },
      { name: "Race Control Messages", tier: "paid+" },
    ],
  },
  lab: {
    type: "lab",
    displayName: "BDL Lab",
    category: "basketball",
    endpoints: [],
  },
  stories_nba: {
    type: "stories_nba",
    displayName: "StoryStats NBA",
    category: "stories",
    endpoints: [],
  },
  stories_wnba: {
    type: "stories_wnba",
    displayName: "StoryStats WNBA",
    category: "stories",
    endpoints: [],
  },
  stories_mlb: {
    type: "stories_mlb",
    displayName: "StoryStats MLB",
    category: "stories",
    endpoints: [],
  },
  stories_nhl: {
    type: "stories_nhl",
    displayName: "StoryStats NHL",
    category: "stories",
    endpoints: [],
  },
  stories_nfl: {
    type: "stories_nfl",
    displayName: "StoryStats NFL",
    category: "stories",
    endpoints: [],
  },
  stories_soccer: {
    type: "stories_soccer",
    displayName: "StoryStats Soccer",
    category: "stories",
    endpoints: [],
  },
  all_access: {
    type: "all_access",
    displayName: "All Access (Legacy)",
    category: "basketball",
    endpoints: [],
  },
  all_access_v2: {
    type: "all_access_v2",
    displayName: "All Access (Legacy V2)",
    category: "basketball",
    endpoints: [],
  },
  all_access_v3: {
    type: "all_access_v3",
    displayName: "All Access",
    category: "basketball",
    endpoints: [],
  },
  all_access_v4: {
    type: "all_access_v4",
    displayName: "All Access (Legacy V4)",
    category: "basketball",
    endpoints: [],
  },
};

// Sports grouped by category for the UI
export const SPORT_CATEGORIES = {
  basketball: ["cba", "nba", "wnba", "ncaab", "ncaaw", "euroleague", "eurocup", "bcl", "acb", "bsl", "gbl", "lba", "bbl", "lnb", "aba", "lkl", "vtb", "bnxt", "israel"] as Type[],
  football: ["nfl", "ncaaf"] as Type[],
  soccer: [
    "epl",
    "laliga",
    "seriea",
    "ucl",
    "bundesliga",
    "ligue1",
    "mls",
    "fifa",
  ] as Type[],
  baseball: ["mlb", "cbb"] as Type[],
  tennis: ["atp", "wta"] as Type[],
  other: ["nhl", "mma", "pga", "f1"] as Type[],
  esports: ["lol", "dota2", "cs", "valorant"] as Type[],
  stories: [
    "stories_nba",
    "stories_wnba",
    "stories_mlb",
    "stories_nhl",
    "stories_nfl",
    "stories_soccer",
  ] as Type[],
};

// Tier display info
export const TIER_INFO = {
  free: {
    name: "FREE",
    price: "$0",
    description: "Basic endpoints",
    colorScheme: "gray",
  },
  paid: {
    name: "ALL-STAR",
    price: "$9.99/mo",
    description: "Extended access",
    colorScheme: "blue",
  },
  "paid+": {
    name: "GOAT",
    price: "$39.99/mo",
    description: "Full access",
    colorScheme: "brand",
  },
};

// Utility to count endpoints by tier
export function countEndpointsByTier(sport: SportPricing) {
  const counts = { free: 0, paid: 0, "paid+": 0 };
  sport.endpoints.forEach((ep) => {
    if (ep.tier === "free") counts.free++;
    else if (ep.tier === "paid") counts.paid++;
    else counts["paid+"]++;
  });
  return counts;
}

// Get endpoints available at a specific tier (cumulative)
export function getEndpointsForTier(sport: SportPricing, tier: Tier): string[] {
  const tierOrder: Tier[] = ["free", "paid", "paid+"];
  const maxIndex = tierOrder.indexOf(tier);
  return sport.endpoints
    .filter((ep) => tierOrder.indexOf(ep.tier) <= maxIndex)
    .map((ep) => ep.name);
}
